import { useCallback, useEffect, useRef, useState } from "react";
import { HomeSummaryEntity } from "../../domain/entities/HomeSummaryEntity";
import { homeContainer } from "../../di/container";

interface UseHomeSummaryReturn {
  summary: HomeSummaryEntity | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Loads the Home dashboard payload once on mount, and on demand via `refresh`.
 *
 * The in-flight request is aborted on unmount, and a stale response can never
 * overwrite fresher state — without that guard, a slow first load landing after
 * a fast refresh would show older counts.
 */
export const useHomeSummary = (): UseHomeSummaryReturn => {
  const [summary, setSummary] = useState<HomeSummaryEntity | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    abortRef.current?.abort();

    const controller = new AbortController();
    abortRef.current = controller;

    const requestId = ++requestIdRef.current;

    setLoading(true);
    setError(null);

    try {
      const result = await homeContainer.getHomeSummary().execute(controller.signal);

      if (requestId === requestIdRef.current) {
        setSummary(result);
      }
    } catch (err: any) {
      if (controller.signal.aborted) {
        return;
      }

      if (requestId === requestIdRef.current) {
        setError(err?.message || "Failed to load your dashboard");
        // eslint-disable-next-line no-console
        console.error("Failed to load home summary:", err);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    load();

    return () => {
      abortRef.current?.abort();
    };
  }, [load]);

  return { summary, loading, error, refresh: load };
};
